import { useState } from "react";
import { ClickAwayListener, Stack, Tooltip, Typography, useMediaQuery, useTheme, Zoom } from "@mui/material";
import { EGIconButton } from "../Button/IconButton/IconButton";
import { BoldFlag } from "../../Icon";

/**
 * Reusable component to display classification tags as colored flag icons.
 * Shows up to `maxVisible` flags, with a "+N" indicator for overflow.
 * Tooltip shows the full list of tags on hover (desktop) or click (mobile).
 *
 * @param {string[]} tags - Array of tag keys (e.g. ["black", "red", "pink"])
 * @param {object} transformedType - Mapping of tag keys to tag catalog data
 * @param {function} getLocalizedLabel - Function to get localized label from tag data
 * @param {string} tooltipTitle - Title shown in the tooltip header
 * @param {number} maxVisible - Max number of flag icons to show (default 3)
 * @param {number} iconSize - Size of the flag icons (default 18)
 * @param {object} sx - Additional sx props for the container Stack
 */
export const TagsPreview = ({
  tags = [],
  transformedType = {},
  getLocalizedLabel,
  tooltipTitle = "",
  maxVisible = 3,
  iconSize = 18,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);

  const tagList = Array.isArray(tags) ? tags : [];
  if (!tagList.length || !transformedType || !getLocalizedLabel) return null;

  const visibleTags = tagList.slice(0, maxVisible);
  const overflowCount = tagList.length - maxVisible;

  const tooltipProps = isMobile
    ? {
        open,
        onClose: () => setOpen(false),
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
      }
    : {};

  const handleClick = (e) => {
    e.stopPropagation();
    if (isMobile) setOpen((prev) => !prev);
  };

  const tooltipContent = (
    <ul style={{ margin: 0, paddingRight: 10, listStyle: "none" }}>
      {tooltipTitle && (
        <Typography color="secondary" ml={-1} variant="body1" fontWeight={600}>
          {tooltipTitle}:
        </Typography>
      )}
      {tagList.map((tag) => (
        <li key={tag}>
          <Typography
            color={theme.palette?.[tag]?.main}
            variant="body1"
            sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              ml: -0.5,
              my: 0.5,
            }}
          >
            <BoldFlag
              color={theme.palette?.[tag]?.main}
              size={18}
              className="mr-10"
            />
            {getLocalizedLabel(transformedType?.[tag])}
          </Typography>
        </li>
      ))}
    </ul>
  );

  const tooltipSx = {
    background: theme.palette.background.paper,
    boxShadow: `0 0 5px -2px ${theme.palette.common.black}`,
    "& .MuiTooltip-arrow": {
      color: theme.palette.background.paper,
    },
  };

  const content = (
    <Tooltip
      arrow
      placement="left-start"
      slots={{ transition: Zoom }}
      slotProps={{
        transition: { timeout: 150 },
        tooltip: { sx: tooltipSx },
      }}
      title={tooltipContent}
      {...tooltipProps}
    >
      <Stack
        spacing={0}
        onClick={handleClick}
        sx={{
          cursor: "pointer",
          alignItems: "center",
          justifyContent: "center",
          flexFlow: "row nowrap",
          ...sx,
        }}
      >
        {visibleTags.map((tag) => (
          <EGIconButton
            key={tag}
            iconName="bold_flag"
            colorHover="transparent"
            iconColor={tag}
            iconSize={iconSize}
            sx={{ mx: -0.7, paddingY: 0 }}
          />
        ))}
        {overflowCount > 0 && (
          <Typography variant="caption" color="secondary" fontSize={15}>
            +{overflowCount}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );

  if (!isMobile) return content;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      {content}
    </ClickAwayListener>
  );
};
